export type EconomyScoreState = 'excelente' | 'alto' | 'moderado' | 'bajo' | 'minimo';

export interface EconomyScoreFactors {
  seguidores: number;
  oyentes: number;
  interaccion: number;
  patrocinadores: number;
  campañas: number;
  eventos: number;
}

export interface RadioEconomyScoreData {
  score: number;
  state: EconomyScoreState;
  factors: EconomyScoreFactors;
  sponsorsCount: number;
  activeCampaigns: number;
}

export const stateConfig: Record<EconomyScoreState, { label: string; color: string; dot: string; ring: string; track: string }> = {
  excelente:  { label: 'Excelente', color: 'text-emerald-600', dot: 'bg-emerald-500', ring: 'text-emerald-500', track: 'text-emerald-100' },
  alto:       { label: 'Alto',      color: 'text-green-600',  dot: 'bg-green-500',  ring: 'text-green-500',  track: 'text-green-100' },
  moderado:   { label: 'Moderado',  color: 'text-amber-600',  dot: 'bg-amber-500',  ring: 'text-amber-500',  track: 'text-amber-100' },
  bajo:       { label: 'Bajo',      color: 'text-orange-600', dot: 'bg-orange-500', ring: 'text-orange-500', track: 'text-orange-100' },
  minimo:     { label: 'Mínimo',    color: 'text-red-600',    dot: 'bg-red-500',    ring: 'text-red-500',    track: 'text-red-100' },
};

export function getEconomyScoreState(score: number): EconomyScoreState {
  if (score >= 80) return 'excelente';
  if (score >= 60) return 'alto';
  if (score >= 40) return 'moderado';
  if (score >= 20) return 'bajo';
  return 'minimo';
}

/* Economy scores mapped to SA radios by id */
export const radioEconomyScores: Record<number, RadioEconomyScoreData> = {
  1: { score: 78, state: 'alto', factors: { seguidores: 72, oyentes: 68, interaccion: 80, patrocinadores: 60, campañas: 75, eventos: 70 }, sponsorsCount: 2, activeCampaigns: 3 },
  2: { score: 38, state: 'bajo', factors: { seguidores: 35, oyentes: 42, interaccion: 38, patrocinadores: 10, campañas: 20, eventos: 25 }, sponsorsCount: 0, activeCampaigns: 0 },
  3: { score: 96, state: 'excelente', factors: { seguidores: 95, oyentes: 92, interaccion: 98, patrocinadores: 90, campañas: 85, eventos: 88 }, sponsorsCount: 5, activeCampaigns: 6 },
  4: { score: 22, state: 'bajo', factors: { seguidores: 20, oyentes: 48, interaccion: 18, patrocinadores: 5, campañas: 10, eventos: 12 }, sponsorsCount: 0, activeCampaigns: 0 },
  6: { score: 74, state: 'alto', factors: { seguidores: 70, oyentes: 72, interaccion: 76, patrocinadores: 55, campañas: 65, eventos: 68 }, sponsorsCount: 2, activeCampaigns: 2 },
  7: { score: 88, state: 'excelente', factors: { seguidores: 85, oyentes: 90, interaccion: 92, patrocinadores: 78, campañas: 82, eventos: 80 }, sponsorsCount: 3, activeCampaigns: 4 },
  8: { score: 58, state: 'moderado', factors: { seguidores: 55, oyentes: 62, interaccion: 52, patrocinadores: 40, campañas: 48, eventos: 50 }, sponsorsCount: 1, activeCampaigns: 1 },
  9: { score: 42, state: 'moderado', factors: { seguidores: 38, oyentes: 45, interaccion: 40, patrocinadores: 20, campañas: 28, eventos: 32 }, sponsorsCount: 0, activeCampaigns: 0 },
  10: { score: 98, state: 'excelente', factors: { seguidores: 96, oyentes: 98, interaccion: 95, patrocinadores: 92, campañas: 90, eventos: 94 }, sponsorsCount: 5, activeCampaigns: 7 },
  11: { score: 32, state: 'bajo', factors: { seguidores: 30, oyentes: 28, interaccion: 35, patrocinadores: 5, campañas: 15, eventos: 20 }, sponsorsCount: 0, activeCampaigns: 0 },
  13: { score: 72, state: 'alto', factors: { seguidores: 68, oyentes: 75, interaccion: 72, patrocinadores: 50, campañas: 62, eventos: 65 }, sponsorsCount: 1, activeCampaigns: 2 },
  14: { score: 15, state: 'minimo', factors: { seguidores: 12, oyentes: 35, interaccion: 10, patrocinadores: 0, campañas: 5, eventos: 8 }, sponsorsCount: 0, activeCampaigns: 0 },
  16: { score: 48, state: 'moderado', factors: { seguidores: 45, oyentes: 38, interaccion: 50, patrocinadores: 25, campañas: 35, eventos: 40 }, sponsorsCount: 0, activeCampaigns: 0 },
  17: { score: 68, state: 'alto', factors: { seguidores: 65, oyentes: 70, interaccion: 68, patrocinadores: 48, campañas: 58, eventos: 60 }, sponsorsCount: 1, activeCampaigns: 2 },
  19: { score: 84, state: 'excelente', factors: { seguidores: 82, oyentes: 88, interaccion: 86, patrocinadores: 72, campañas: 78, eventos: 75 }, sponsorsCount: 3, activeCampaigns: 4 },
  20: { score: 56, state: 'moderado', factors: { seguidores: 52, oyentes: 55, interaccion: 58, patrocinadores: 38, campañas: 42, eventos: 45 }, sponsorsCount: 1, activeCampaigns: 1 },
};

/* Top radios sorted by economy score for dashboard */
export function getTopEconomyRadios(): { id: number; name: string; frequency: string; city: string; score: number; state: EconomyScoreState }[] {
  const radiosMeta: Record<number, { name: string; frequency: string; city: string }> = {
    1: { name: 'Radio Nueva Actitud', frequency: '94.70 RFW', city: 'Santa Rosa' },
    2: { name: 'Radio Ritmo Latino', frequency: '101.30 RFW', city: 'San José' },
    3: { name: 'Radio Éxitos TV', frequency: '94.70 RFW', city: 'Ciudad de México' },
    4: { name: 'Radio Fiesta Mix', frequency: '88.50 RFW', city: 'Buenos Aires' },
    6: { name: 'Radio Sonora Digital', frequency: '103.70 RFW', city: 'Hermosillo' },
    7: { name: 'Radio Stereo Visión', frequency: '96.10 RFW', city: 'Medellín' },
    8: { name: 'Radio Clásica FM', frequency: '88.90 RFW', city: 'Santiago' },
    9: { name: 'Radio Urbana 101', frequency: '101.10 RFW', city: 'Caracas' },
    10: { name: 'Radio Élite Digital', frequency: '107.50 RFW', city: 'Madrid' },
    11: { name: 'La Voz del Llano', frequency: '92.30 RFW', city: 'Villavicencio' },
    13: { name: 'Radio Festival TV', frequency: '100.20 RFW', city: 'Quito' },
    14: { name: 'Radio Brisas del Mar', frequency: '89.70 RFW', city: 'Acapulco' },
    16: { name: 'Radio Folclor Andino', frequency: '91.40 RFW', city: 'Cusco' },
    17: { name: 'Radio Retro Hits', frequency: '98.60 RFW', city: 'Asunción' },
    19: { name: 'Radio Deportiva Total', frequency: '93.80 RFW', city: 'Guadalajara' },
    20: { name: 'Radio Gospel Internacional', frequency: '87.90 RFW', city: 'Miami' },
  };

  return Object.entries(radioEconomyScores)
    .map(([id, data]) => {
      const meta = radiosMeta[Number(id)];
      return { id: Number(id), name: meta?.name ?? 'Desconocida', frequency: meta?.frequency ?? '—', city: meta?.city ?? '—', score: data.score, state: data.state };
    })
    .sort((a, b) => b.score - a.score);
}